import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import { secureFetch } from '../utils/secureFetch';

// Context para compartir favoritos globalmente
const FavoritesContext = createContext();

// Provider para envolver la app
export const FavoritesProvider = ({ children }) => {
    const [user] = useAuthState(auth);
    const [favorites, setFavorites] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [lastFetch, setLastFetch] = useState(0);

    // Obtener userId de localStorage como en Product.js original
    const usuario = (() => {
        try {
            return JSON.parse(localStorage.getItem('usuario') || 'null');
        } catch (e) {
            console.error('Error parsing usuario from localStorage:', e);
            return null;
        }
    })();
    const userId = usuario?.id;

    // Cargar favoritos una sola vez
    const loadFavorites = useCallback(async (forceRefresh = false) => {
        if (!userId) {
            setFavorites(new Set());
            return;
        }

        // Throttling: solo recargar cada 30 segundos
        const now = Date.now();
        if (!forceRefresh && now - lastFetch < 30000) {
            return;
        }

        setLoading(true);
        try {
            const res = await secureFetch(`https://api.minymol.com/favorites/${userId}`);

            if (!res.ok) {
                if (res.status === 429) {
                    return; // Silenciar rate limiting
                }
                if (res.status === 400) {
                    console.error('Error 400 en favoritos - posible problema con userId:', userId);
                    setFavorites(new Set()); // Limpiar favoritos si hay error 400
                    return;
                }
                throw new Error(`HTTP ${res.status}`);
            }

            const data = await res.json();
            const favoriteIds = new Set(data.map(fav => fav.id));
            setFavorites(favoriteIds);
            setLastFetch(now);
        } catch (err) {
            if (!err.message?.includes('429')) {
                console.error('Error cargando favoritos:', err);
            }
        } finally {
            setLoading(false);
        }
    }, [userId, lastFetch]); // Solo las dependencias que realmente se usan

    // Cargar favoritos cuando cambie el usuario
    useEffect(() => {
        if (user && userId) {
            loadFavorites();
        } else {
            setFavorites(new Set());
        }
    }, [user, userId, loadFavorites]); // Depender tanto de Firebase auth como del userId de localStorage

    // Toggle favorito (optimista)
    const toggleFavorite = async (productId) => {
        if (!userId) return;

        const wasFavorite = favorites.has(productId);

        // Actualización optimista
        const newFavorites = new Set(favorites);
        if (wasFavorite) {
            newFavorites.delete(productId);
        } else {
            newFavorites.add(productId);
        }
        setFavorites(newFavorites);

        try {
            if (wasFavorite) {
                const res = await secureFetch(`https://api.minymol.com/favorites/${userId}/${productId}`, {
                    method: 'DELETE',
                });

                if (!res.ok && res.status !== 429) {
                    throw new Error(`HTTP ${res.status}`);
                }
            } else {
                const res = await secureFetch('https://api.minymol.com/favorites', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId, productId }),
                });

                if (!res.ok && res.status !== 429) {
                    throw new Error(`HTTP ${res.status}`);
                }
            }
        } catch (err) {
            // Revertir cambio optimista si falla
            setFavorites(favorites);

            if (!err.message?.includes('429')) {
                console.error('Error al cambiar favorito:', err);
            }
        }
    };

    const value = {
        favorites,
        loading,
        isFavorite: (productId) => favorites.has(productId),
        toggleFavorite,
        refreshFavorites: () => loadFavorites(true),
    };

    return (
        <FavoritesContext.Provider value={value}>
            {children}
        </FavoritesContext.Provider>
    );
};

// Hook para usar en componentes
export const useFavorites = () => {
    const context = useContext(FavoritesContext);
    if (!context) {
        throw new Error('useFavorites debe usarse dentro de FavoritesProvider');
    }
    return context;
};